<template>
  <BTabs class="mb-2">
    <BTab ref="today"
          active
          :title="tr('javascript.TX_TODAY')"
          @click="onTabTodayClick"></BTab>
    <BTab :title="tr('javascript.TX_YESTERDAY')"
          @click="onTabYesterdayClick"></BTab>
    <BTab :title="tr('performanceOverview.LAST_SEVEN_DAYS')"
          @click.prevent="onTabWeekClick"></BTab>
    <BTab v-if="shouldShowLastYear"
          :title="tr('alarmview.LAST_YEAR')"
          @click.prevent="onTabLastYearClick"></BTab>
    <BTab v-if="shouldShowArchive"
          :title="tr('alarmview.ARCHIVE')"
          @click.prevent="onTabArchiveClick"></BTab>
    <BTab v-if="shouldShowMonth"
          id="monthPickerTab"
          :title="tr('javascript.TX_MONTH')"
          @click="onTabMonthClick">
      <DatePicker ref="monthpicker"
                  :model-value="dateRangeEnd"
                  :disabled-dates="disabledDates"
                  type="month"
                  invisible-input
                  :custom-position="() => datePickerPosition('monthPickerTab')"
                  @update:model-value="onMonthSelected">
      </DatePicker>
    </BTab>
    <BTab v-if="shouldShowYear"
          id="yearpickerTab"
          :title="tr('javascript.TX_YEAR')"
          @click="onTabYearClick">
      <DatePicker ref="yearpicker"
                  :model-value="dateRangeEnd"
                  :disabled-dates="disabledDates"
                  type="year"
                  invisible-input
                  :custom-position="() => datePickerPosition('yearpickerTab')"
                  @update:model-value="onYearSelected">
      </DatePicker>
    </BTab>
    <BTab v-if="shouldShowCustom"
          id="customPickerTab"
          :title="tr('common.USER_DEFINED')"
          @click="onTabCustomClick">
      <DatePicker ref="daterangepicker"
                  :model-value="rangeSelection"
                  :range-configuration="{ maxRange: maxTimeRange }"
                  :disabled-dates="disabledDates"
                  invisible-input
                  :custom-position="() => datePickerPosition('customPickerTab')"
                  @update:model-value="onCustomDateSelected">
        <template #top-extra>
          <AlertMessage v-if="(maxTimeRange > 0)"
                        :message="tr('common.DATERANGEPICKER_MAX_RANGE', [maxTimeRange])"
                        :message-is-html="true"
                        :variant="BootstrapVariant.INFO"
                        class="mb-0 mt-0"></AlertMessage>
        </template>
      </DatePicker>
    </BTab>
    <template #tabs-end>
      <li v-if="isDateRangeVisible"
          class="ml-3 nav-item align-self-center"
          v-html="getDateRange">
      </li>
      <div v-if="shouldShowCsvExport"
           class="ml-auto">
        <CsvExportButton :size="BootstrapVariant.SMALL"
                         class="ml-2"
                         @click="onCsvClick"></CsvExportButton>
      </div>
    </template>
  </BTabs>
</template>

<script>
  import { BTabs, BTab } from 'bootstrap-vue';
  import makeMoment from '@common/moment/MomentFactory';
  import BootstrapVariant from '~/zf/frontend/vendor/bootstrap/BootstrapVariant';
  import { translator } from '~/zf/frontend/common/mixins';
  import TimeRange from './TimeRange';
  import EventService from '~/zf/frontend/common/event/EventService';
  import PortalEvent from '~/zf/frontend/vcom/events/PortalEvent';
  import CsvExportButton from '../ag-grid/component/CsvExportButton.vue';
  import DatePicker from '../datepicker/DatePicker.vue';
  import AlertMessage from '../layout/AlertMessage.vue';

  export default {
    name: 'TabSelection',
    compatConfig: {
      MODE: 3,
    },
    components: {
      DatePicker,
      CsvExportButton,
      BTabs,
      BTab,
      AlertMessage,
    },
    mixins: [translator],
    props: {
      dateRangeStart: {
        type: String,
      },
      dateRangeEnd: {
        type: String,
      },
      shouldShowLastYear: {
        type: Boolean,
      },
      shouldShowArchive: {
        type: Boolean,
      },
      shouldShowCsvExport: {
        type: Boolean,
        default: true,
      },
      shouldShowMonth: {
        type: Boolean,
      },
      shouldShowYear: {
        type: Boolean,
        default: false,
      },
      shouldShowCustom: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['change', 'on-csv-click'],
    data() {
      return {
        BootstrapVariant,
        disabledDates: {
          from: new Date(),
        },
        maxTimeRange: 366,
        timeRange: null,
        isDateRangeVisible: false,
      };
    },
    computed: {
      getDateRange() {
        const endDate = makeMoment(this.dateRangeEnd);
        const startDate = makeMoment(this.dateRangeStart);
        const label = this.tr('javascript.TX_DATERANGE');

        return `<b>${label}:</b> ${startDate.format('L')} – ${endDate.format('L')}`;
      },
      rangeSelection() {
        if (this.dateRangeStart && this.dateRangeEnd) {
          return [this.dateRangeStart, this.dateRangeEnd];
        }
        return [];
      },
    },
    created() {
      EventService.addListener(PortalEvent.CURRENT_MONTH_PASSED, { callback: this.refreshMaxDate });
    },
    unmounted() {
      EventService.removeListener(PortalEvent.CURRENT_MONTH_PASSED, { callback: this.refreshMaxDate });
    },
    methods: {
      datePickerPosition(tabElementId) {
        const tabElement = document.getElementById(`${tabElementId}___BV_tab_button__`);
        if (!tabElement) {
          return null;
        }

        const popUpWidth = tabElementId === 'customPickerTab' ? 290 : 260;
        return { top: 10, left: tabElement.offsetLeft + tabElement.clientWidth / 2 - popUpWidth / 2 };
      },
      getEndOfMonthDate(value) {
        const endDate = makeMoment(value);
        const isCurrentMonth = makeMoment().format('Y-M') === endDate.format('Y-M');
        const pickerEndDate = isCurrentMonth === true ? makeMoment() : endDate.endOf('month');
        return pickerEndDate.format('Y-M-D');
      },
      getEndOfYearDate(value) {
        const endDate = makeMoment(value);
        const isCurrentYear = makeMoment().format('Y') === endDate.format('Y');
        const pickerEndDate = isCurrentYear === true ? makeMoment() : endDate.endOf('year');
        return pickerEndDate.format('Y-M-D');
      },
      onTabTodayClick() {
        this.emitTabChanged(TimeRange.TODAY);
      },
      onTabYesterdayClick() {
        this.emitTabChanged(TimeRange.YESTERDAY);
      },
      onTabWeekClick() {
        this.emitTabChanged(TimeRange.LAST_WEEK);
      },
      onTabMonthClick() {
        this.$nextTick(() => {
          this.$refs.monthpicker.showCalendar();
          this.$refs.monthpicker.$el.querySelector('input').focus();
        });
      },
      onTabYearClick() {
        this.$nextTick(() => {
          this.$refs.yearpicker.showCalendar();
          this.$refs.yearpicker.$el.querySelector('input').focus();
        });
      },
      onTabCustomClick() {
        this.$nextTick(() => {
          this.$refs.daterangepicker.showCalendar();
          this.$refs.daterangepicker.$el.querySelector('input').focus();
        });
      },
      onTabLastYearClick() {
        this.emitTabChanged(TimeRange.LAST_YEAR);
      },
      onTabArchiveClick() {
        this.emitTabChanged(TimeRange.ARCHIVE);
      },
      onMonthSelected(value) {
        this.emitTabChanged(TimeRange.MONTH, this.getEndOfMonthDate(value));
      },
      onYearSelected(value) {
        this.emitTabChanged(TimeRange.YEAR, this.getEndOfYearDate(value));
      },
      onCustomDateSelected(dateRange) {
        this.emitTabChanged(TimeRange.CUSTOM, makeMoment(dateRange[1]).format('Y-M-D'), makeMoment(dateRange[0]).format('Y-M-D'));
      },
      emitTabChanged(timeRange, endDate, startDate) {
        this.isDateRangeVisible = this.dateRangeEnd && [TimeRange.LAST_WEEK, TimeRange.MONTH, TimeRange.YEAR, TimeRange.LAST_YEAR, TimeRange.CUSTOM].includes(timeRange);
        this.$emit('change', timeRange, endDate, startDate);
      },
      onCsvClick() {
        this.$emit('on-csv-click');
      },
      refreshMaxDate() {
        this.disabledDates = {
          from: new Date(),
        };
      },
    },
  };
</script>
